"use client"

import { Grid2x2, List } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import type { DataTableColumn, DataTableProps } from "./DataTable.types"

export default function DataTable<T extends Record<string, any>>({
  columns,
  data,
  rowKey = "id",
  className,
  rowClassName = "bg-[#f7f7f7] shadow-[0_4px_6px_rgba(0,0,0,0.1)] hover:bg-[#f7f7f7] border-none rounded-2xl overflow-hidden",
  onRowClick,
  emptyState,
  loading = false,
}: DataTableProps<T>) {
  const getRowKey = (item: T, index: number) => {
    if (typeof rowKey === "function") {
      return rowKey(item)
    }
    return item[rowKey] as string | number
  }

  const getRowClassName = (item: T, index: number) => {
    if (typeof rowClassName === "function") {
      return rowClassName(item, index)
    }
    return rowClassName
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  if (data.length === 0 && emptyState) {
    return <div className="p-8">{emptyState}</div>
  }

  return (
    <div className={cn("w-full", className)}>
      <Table className="border-separate border-spacing-y-4">
        <TableHeader>
          <TableRow className="border-0 hover:bg-transparent">
            {columns.map((column, columnIndex) => (
              <TableHead
                key={columnIndex}
                className={cn("font-bold", column.className)}
                style={{ width: column.width }}
              >
                {column.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>

        <TableBody>
          {data.map((item, index) => (
            <TableRow
              key={getRowKey(item, index)}
              className={cn(
                getRowClassName(item, index),
                onRowClick && "cursor-pointer"
              )}
              onClick={() => onRowClick?.(item, index)}
            >
              {columns.map((column, columnIndex) => {
                const value = item[column.key as keyof T]
                const isFirstColumn = columnIndex === 0
                const isLastColumn = columnIndex === columns.length - 1

                return (
                  <TableCell
                    key={columnIndex}
                    className={cn(
                      "border-y border-[#d5d5d5]",
                      isFirstColumn && "rounded-tl-2xl rounded-bl-2xl border-l text-center",
                      isLastColumn && "rounded-tr-2xl rounded-br-2xl border-r",
                      column.className
                    )}
                  >
                    {column.render ? (
                      column.render(value, item, index)
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="truncate">{value?.toString()}</span>
                      </div>
                    )}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
